/* eslint-disable no-nested-ternary */

/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';

import { RootState } from '../../../../../redux/store';
import { setEmployeeFreqReportSort } from '../../../../../redux/actions';

import { logRoundProfileFromReports } from '../../../../../utils/analyticsHelpers';

/**
 * Global Type Definition Imports
 */
import { EmpFreq } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface EmployeeFrequencyReportTableProps {
  loading: boolean;
  frequencyReportList: EmpFreq[];
  totalTableCount: number;
}
const EmployeeFrequencyReportTable: FC<EmployeeFrequencyReportTableProps> = ({
  loading,
  frequencyReportList,
  totalTableCount
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('employeeDept');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Department');

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Department':
        key = 'employeeDept';
        break;
      case 'Employee':
        key = 'employeeName';
        break;
      case 'Round Total':
        key = 'total';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEmployee = (round: EmpFreq) => {
    logTrackingEvent(logRoundProfileFromReports('employee'));
    navigate('/employees/profile', {
      state: {
        from: pathname,
        employeeId: round.employeeId,
        mode: undefined
      }
    });
  };

  const isTableEmpty = useMemo(
    () => frequencyReportList.length === 0,
    [frequencyReportList]
  );

  useEffect(() => {
    return () => {
      dispatch(
        setEmployeeFreqReportSort({ sortKey: 'employeeDept', sortDir: 'asc' })
      );
    };
  }, []);

  useEffect(() => {
    dispatch(setEmployeeFreqReportSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Department"
      direction={!(active === 'Department') ? 'none' : sortDir}
      id="Employee-Freq-Report-Table-Department-Column"
      unit="column"
      colWidth={42}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Employee"
      id="Employee-Freq-Report-Table-Employee-Column"
      unit="column"
      colWidth={42}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Round Total"
      id="Employee-Freq-Report-Table-Round-Total-Column"
      unit="column"
      colWidth={16}
      direction={!(active === 'Round Total') ? 'none' : sortDir}
      sort
    >
      Round Total
    </CellContainer>
  ];

  const rows = frequencyReportList?.map((round: EmpFreq, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Employee-Freq-Report-Row-${i}`}
        id={`Employee-Freq-Report-Row-${round.employeeId}:${round?.department}`}
        unit="row"
        size="default"
      >
        <CellContainer unit="column" colWidth={42}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {round.department}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={42}>
          <StyledLabel
            role="button"
            style={{ whiteSpace: 'normal' }}
            onClick={() => openEmployee(round)}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  color: '#206DA3',
                  fontWeight: 700
                }}
              >
                {round.employee}
              </div>
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={16}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              <div style={{ marginLeft: '3rem' }}>{round.count}</div>
            </div>
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="employee-freq-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={loading}
      totalResults={totalTableCount}
      type="employeeFrequencyReport"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    loading: state.ReportReducer.loading,
    modalType: state.ModalReducer.modalType,
    totalTableCount: state.ReportReducer.totalTableCount
  };
};

export default connect(mapReduxStateToProps)(EmployeeFrequencyReportTable);
